
import {
	Clock,
	HemisphereLight,
	IcosahedronGeometry,
	Mesh,
	MeshBasicMaterial,
	OrthographicCamera,
	Points,
	Raycaster,
	Scene,
	ShaderMaterial,
	SphereGeometry,
	Spherical,
	TextureLoader,
	Vector2,
	Vector3,
	WebGLRenderer,
	MathUtils
} from "three"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls"




function createScene(canvas, container, popupEl, globeTexUrl, locationData) {

    const renderer = new WebGLRenderer({ canvas, alpha: true })
    renderer.setPixelRatio(2)

    const scene = new Scene()
    const camera = new OrthographicCamera(-1.1, 1.1, 1.1, -1.1, 0, 3)
    camera.position.z = 1.5

    const rayCaster = new Raycaster()
    rayCaster.far = 2

    const mousePos = new Vector2(-1, -1)
    const clock = new Clock()

    const orbitControls = createOrbitControls(canvas, camera)
    const popup = createPopup(popupEl, canvas, camera)

    const components = {
        canvas, renderer, scene, camera, rayCaster, 
        mousePos, clock, orbitControls, popup,
        globe: null,
        locationPoints: [],
    }

    new TextureLoader().load(globeTexUrl, mapTex => {
        mapTex.repeat.set(1, 1)
        components.globe = createGlobe(scene, mapTex)
        components.locationPoints = createLocations(canvas, scene, locationData)
        updateSize()
        renderLoop()

        const light = new HemisphereLight(0xFFFFFF, 0xAAAAAA, 1);
        scene.add(light)
    })

    function updateSize() {
        const minSide = Math.min(Math.floor(container.offsetWidth), Math.floor(container.offsetHeight))
        canvas.style.width = minSide + "px"
        canvas.style.height = minSide + "px"
        renderer.setSize(minSide, minSide)
        components.globe.dotMaterial.uniforms.u_dot_size.value = .025 * minSide
    }

    const renderCallbacks = []
    function renderLoop() {
        renderCallbacks.map(fn => fn(components))
        orbitControls.controls.update()
        renderer.render(scene, camera)
        requestAnimationFrame(renderLoop)
    }

    window.addEventListener("resize", updateSize)

    canvas.addEventListener('mousemove', e => {
        updateMousePosition(mousePos, e.currentTarget, e.clientX, e.clientY)
    })

    addEvents(components)

    return {
        canvas,
        components,
        onBeforeRender(fn) {
            renderCallbacks.push(fn)
        }
    }
}

function createOrbitControls(canvas, camera) {
    
    // start at UK
    camera.position.x = -1.4235959624018277
    camera.position.y = 0.4005007205180994
    camera.position.z = 0.250945628967098

    const controls = new OrbitControls(camera, canvas)
    controls.enablePan = false
    controls.enableZoom = false
    controls.enableDamping = true
    controls.minPolarAngle = 0.3 * Math.PI
    controls.maxPolarAngle = 0.6 * Math.PI
    controls.autoRotate = true
    controls.autoRotateSpeed = 0.8

    let timestamp, dragged = false

    function onStart() {
        timestamp = Date.now()
    }

    function onEnd() {
        dragged = (Date.now() - timestamp) > 300
    }
    controls.addEventListener("start", onStart)
    controls.addEventListener("end", onEnd)

    return {
        controls, 
        get dragged() {
            return dragged
        }
    }
}

function createGlobe(scene, mapTex) {
    
    const dotMaterial = new ShaderMaterial({
        vertexShader: `
            uniform sampler2D u_map_tex;
            uniform float u_dot_size;

            varying float vOpacity;
            varying vec2 vUv;

            void main() {
                vUv = uv;
                // mask with world map
                float visibility = step(.2, texture2D(u_map_tex, uv).r);
                gl_PointSize = visibility * u_dot_size;

                // make back dots semi-transparent
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                vOpacity = (1. / length(mvPosition.xyz) - .7);
                vOpacity = clamp(vOpacity, .03, 1.);

                gl_Position = projectionMatrix * modelViewMatrix * vec4(position * 1.025, 1.);
            }
        `,
        fragmentShader: `
            uniform sampler2D u_map_tex;

            varying float vOpacity;
            varying vec2 vUv;

            void main() {
                float dot = 1. - smoothstep(.38, .4, length(gl_PointCoord.xy - vec2(.5)));
                if (dot < 0.5) discard;
                gl_FragColor = vec4(0.6,0.6,0.6, dot * vOpacity);
            }
        `,
        uniforms: {
            u_map_tex: { type: "t", value: mapTex },
            u_dot_size: { type: "f", value: 0 },
        },
        alphaTest: false,
        transparent: true
    })

    const globeGeometry = new IcosahedronGeometry(1, 26)
    const points = new Points(globeGeometry, dotMaterial)
    scene.add(points)

    const globeMesh = new Mesh(globeGeometry, new MeshBasicMaterial({
        color: 0x333333,
        transparent: true,
        opacity: .05
    }))
    scene.add(globeMesh)

    return {
        points, 
        dotMaterial,
        globeMesh
    }
}

function createLocations(canvas, scene, locationData) {

    const height = 1.05

    const locationPointsMaterial = new ShaderMaterial({
        vertexShader: `
            varying float vOpacity;
            void main() {
                // vertex in view space (camera is at 0,0,0)
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                vec3 camToVertex = normalize(mvPosition.xyz);

                vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
                vec3 originToVertexView = normalize((viewMatrix * vec4(worldPos, 0.0)).xyz);
            
                float opacity = clamp(1.0 - dot(originToVertexView, camToVertex), 0.15, 1.);
                vOpacity = smoothstep(0.1, 0.6, opacity);
                
                gl_Position = projectionMatrix * mvPosition;
            }
        `,
        fragmentShader: `
            varying float vOpacity;
            void main() {
                vec3 baseColor = vec3(0.902, 0.0, 0.0);
                gl_FragColor = vec4(baseColor, vOpacity);
            }
        `,
        alphaTest: false,
        transparent: true
    })

    const SPHERE_RADIUS_SMALL = 0.04
    const SPHERE_RADIUS_LARGE = 0.08
    const BREAKPOINT = 500

    function sphereSize() {
        return window.innerWidth < BREAKPOINT ? SPHERE_RADIUS_LARGE : SPHERE_RADIUS_SMALL
    }

    const meshes = locationData.map(loc => {
        // adjust longitude by 90 deg to fit to map uv
        const coords = geographicToCartesian(height, loc.lat, loc.lng + 90)
        const sphere = new SphereGeometry(sphereSize(), 12, 12)
        const mesh = new Mesh(sphere, locationPointsMaterial)
        mesh.position.set(coords.x, coords.y, coords.z)
        mesh.opacity = 0.5
        scene.add(mesh)
        mesh.userData.location = loc
        return mesh
    })

    function updateMeshSize(mesh, radius) {
        mesh.geometry.dispose() // free the old geometry
        mesh.geometry = new SphereGeometry(radius, 12, 12)
    }

    window.addEventListener('resize', () => {
        const size = sphereSize()
        meshes.map(mesh => updateMeshSize(mesh, size))
    })

    return meshes
}

function createPopup(popup, canvas, camera) {
    let selectedPos
    let renderId = null
    let show = false

    function showFor(position, loc) {
        selectedPos = position
        popup.innerHTML = loc.link ? `
            <a class="globe-link" target="${loc.target}" href="${loc.link}">
                ${loc.name}
                <div class="globe-link__icon">
                    <div class="icon-arrow-circle-right__circle icon-colour--primary">
                        <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path class="icon-arrow-circle-right__arrow icon-colour--secondary icon-arrow-circle-right__primary" d="M1 1L5.5 5.5L1 10" stroke="black" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path class="icon-arrow-circle-right__arrow icon-colour--secondary icon-arrow-circle-right__secondary" d="M1 1L5.5 5.5L1 10" stroke="black" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                    </div>
                </div>
            </a>
        ` : `
            <div class="globe-link">${loc.name}</div>
        `
        show = true
        popup.style.pointerEvents = ''
        if (!renderId) startLoop()
    }

    function hide() {
        show = false
        popup.style.pointerEvents = 'none'
    }

    function startLoop() {
        const vec2 = toCanvasXY(canvas, camera, selectedPos)
        popup.style.opacity = show 
            ? smoothstep(0.1, 0.6, dotProductFacingCamera(camera, selectedPos))
            : 0
        popup.style.top = `${vec2.y}px`
        popup.style.left = `${vec2.x}px`
        renderId = requestAnimationFrame(startLoop)
    }

    function stopLoop() {
        cancelAnimationFrame(renderId)
        renderId = null
    }

    return {
        showFor, hide
    }
}


function addEvents(components) {
    const { canvas, mousePos, popup } = components 

    canvas.addEventListener('click', e => {
        updateMousePosition(
            mousePos, e.currentTarget,
            e.targetTouches ? e.targetTouches[0].pageX : e.clientX,
            e.targetTouches ? e.targetTouches[0].pageY : e.clientY,
        )
        
        const res = checkIntersects(components)
        if (!res.length) {
            popup.hide()
            return
        }

        const object = res[0].object
        popup.showFor(object.position, object.userData.location)
    })
}

function updateMousePosition(mousePos, el, x, y) {
  const rect = el.getBoundingClientRect()
  const nx = (x - rect.left) / rect.width
  const ny = (y - rect.top) / rect.height
  mousePos.x = nx * 2 - 1
  mousePos.y = - (ny * 2 - 1)
}

function checkIntersects({ rayCaster, globe, mousePos, camera, locationPoints }) {
  camera.updateMatrixWorld()
  rayCaster.setFromCamera(mousePos, camera)
  const intersects = rayCaster.intersectObjects([globe.globeMesh, ...locationPoints], true)
  return intersects.filter(intersect => intersect.object !== globe.globeMesh)
}

function dotProductFacingCamera(camera, position) {
    const cameraPos = camera.position.clone().normalize()
    return cameraPos.dot(position.clone().normalize())
}

function toCanvasXY(canvas, camera, position) {
    const rect = canvas.getBoundingClientRect()
    const widthHalf = rect.width / 2
    const heightHalf = rect.height / 2
    const pos = position.clone()
    pos.project(camera)
    return {
        x:(pos.x*widthHalf) + widthHalf, 
        y:-(pos.y*heightHalf) + heightHalf
    }
}

function geographicToCartesian(radius, lat, lon) {
    const spherical = new Spherical(
        radius,
        MathUtils.degToRad(90 - lat),
        MathUtils.degToRad(lon + 180)
    )
    const vector = new Vector3()
    return vector.setFromSpherical(spherical)
}

function smoothstep(min, max, x) {
    if (x <= min) return 0;
    if (x >= max) return 1;
    x = (x - min) / (max - min)
    return x * x * (3 - 2 * x)
}



// init

window.addEventListener('elementor/frontend/init', () => {
    elementorFrontend.hooks.addAction('frontend/element_ready/globe.default', $scope => {
        const container = $scope[0] || $scope
        const el = container.querySelector('.js-globe')
        const data = JSON.parse(el.querySelector('.js-globe__script')?.textContent || '{}')
        const globeTex = data.texUrl
        const locations = Array.isArray(data.locations) ? data.locations : []
        if (!globeTex) return

        const canvas = el.querySelector('#globe-3d')
        const canvasContainer = el.querySelector('.globe-canvas-container')
        const popup = canvasContainer.querySelector('.globe-canvas-container__popup')
        const scene = createScene(canvas, canvasContainer, popup, globeTex, locations)
    })
})


